/* eslint-disable @typescript-eslint/no-explicit-any */
import { SorterResult } from 'antd/es/table/interface'
import { IAccount } from 'app/api/account/model'
import {
  IParamsGetTradeUnionMember,
  IParamsGetTradeUnionMemberHRM,
  ITradeUnionMember,
} from 'app/api/tradeUnionMembers/model'
import R from 'app/assets/R'
import { ENV_CONFIG } from 'app/common/config'
import { PermissionEnum } from 'app/common/enum'
import { ISort } from 'app/common/interface'
import { BaseSpace } from 'app/components/common/BaseSpace'
import { BaseSwitch } from 'app/components/common/BaseSwitch'
import BaseText from 'app/components/common/BaseText'
import { BaseTooltip } from 'app/components/common/BaseTooltip'
import { convertColumnTable } from 'app/components/tables/BaseTableReport/hook'
import { FORMAT_DD_MM_YYYY_HH_MM } from 'app/constant'
import { useVerifyPermission } from 'app/helpers/permission.helper'
import { useDebounce, usePagination } from 'app/hook'
import {
  useGetHrmTradeUnionMember,
  useUpdateHrmTum,
  useUpdateIsSyncAllHrmTum,
} from 'app/react-query/hook/tumHrm'
import { IResponseQueryList } from 'app/react-query/model/common'
import { handleDownloadExcel } from 'app/service/general/service'
import { isEqual } from 'lodash'
import { Pagination, initialPagination, moment } from 'parkway-web-common'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IDataTable } from './type'

export const useHook = () => {
  const { t } = useTranslation()
  const [pagination, setPagination] = useState<Pagination>(initialPagination)
  const [filter, setFilter] = useState<IParamsGetTradeUnionMemberHRM>({})
  const [sorts, setSort] = useState<ISort[]>([])

  const filterDebounce = useDebounce(filter, 500)

  const { flattenDataList } = usePagination()
  const { data: dataApi, isLoading } = useGetHrmTradeUnionMember({
    search: filterDebounce?.search,
    page: pagination?.current,
    limit: pagination?.pageSize,
    isSync: filterDebounce?.isSync,
    unit: filterDebounce?.unit,
    sorts,
  })

  const { mutateAsync: update, isLoading: isLoadingUpdate } = useUpdateHrmTum()
  const { mutateAsync: updateAll, isLoading: isLoadingUpdateMany } =
    useUpdateIsSyncAllHrmTum()

  const flattenData = useMemo(() => {
    const res: IResponseQueryList<ITradeUnionMember[]> =
      flattenDataList(dataApi)

    return res
  }, [dataApi])

  const data: IDataTable[] = useMemo(() => {
    return (
      flattenData?.data?.map((item, index) => ({
        ...item,
        key: index + 1,
      })) ?? []
    )
  }, [flattenData])

  const total = useMemo(() => {
    return flattenData?.total ?? data?.length
  }, [flattenData, data])

  const onChangeFilter = (newFilter: IParamsGetTradeUnionMember) => {
    setFilter(newFilter)
    setPagination(prev => ({ ...prev, current: 1 }))
  }

  const { user, verifyPermissionAndRedirect, verifyPermission } =
    useVerifyPermission()

  useEffect(() => {
    verifyPermissionAndRedirect(PermissionEnum.ReadTumHrm)
  }, [user])

  const isPermissionSyncFromUeh = useMemo(() => {
    return verifyPermission(PermissionEnum.SyncTradeUnionMember)
  }, [user])

  const isPermissionExportExcelFromUeh = useMemo(() => {
    return verifyPermission(PermissionEnum.ExportTumHrm)
  }, [user])

  const isPermissionUpdate = useMemo(() => {
    return verifyPermission(PermissionEnum.UpdateTumHrm)
  }, [user])

  const handleUpdate = async (record: ITradeUnionMember) => {
    await update({ id: record?.id, isSync: !record.isSync })
  }

  const isSyncPage = useMemo(() => {
    return isEqual(flattenData?.metadata?.totalIsSync ?? 0, total)
  }, [flattenData?.metadata?.totalIsSync, total])

  const handleUpdateIsSync = async () => {
    await updateAll({
      status: isSyncPage ? 0 : 1,
      search: filterDebounce?.search,
      unit: filterDebounce?.unit,
      sorts,
      isSync: filterDebounce?.isSync,
    })
  }

  const columns = useMemo(() => {
    return [
      convertColumnTable<ITradeUnionMember>({
        key: 'fullName',
        title: t(R.strings.full_name),
        fixed: 'left',
        sorter: true,
        render: (_, record) => {
          return (
            <BaseText
              fontWeight="semibold"
              children={record?.fullName ?? '-'}
            />
          )
        },
      }),
      convertColumnTable<ITradeUnionMember>({
        key: 'mql',
        title: t(R.strings.id_management),
        fixed: 'left',
        sorter: true,
        render: (_, record) => {
          return (
            <BaseText fontWeight="semibold" children={record?.mql ?? '-'} />
          )
        },
      }),
      convertColumnTable<ITradeUnionMember>({
        key: 'email',
        title: t(R.strings.email),
        sorter: true,
        render: (_, record) => {
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={record?.email ?? '-'}
            />
          )
        },
      }),
      convertColumnTable<ITradeUnionMember>({
        key: 'tenDonVi',
        title: 'Đơn vị',
        sorter: true,
        render: text => {
          return <BaseText fontWeight="medium" opacity="0.7" children={text} />
        },
      }),

      convertColumnTable<ITradeUnionMember>({
        key: 'gioiTinh',
        title: t(R.strings.gender),
        sorter: true,
        render: (_, record) => {
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={record?.gioiTinh ?? '-'}
            />
          )
        },
      }),

      convertColumnTable<ITradeUnionMember>({
        key: 'dienThoaiDiDong',
        title: t(R.strings.phone),
        sorter: true,
        render: (_, record) => {
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={record?.dienThoaiDiDong ?? '-'}
            />
          )
        },
      }),

      convertColumnTable<{ last_update_by: IAccount }>({
        key: 'last_update_by',
        title: 'Người cập nhật lần cuối',
        render: (_, record) => {
          return (
            <BaseSpace size={2}>
              <BaseText
                fontWeight="semibold"
                children={record?.last_update_by?.full_name ?? '-'}
              />
              <BaseText
                fontWeight="medium"
                opacity="0.7"
                children={record?.last_update_by?.email ?? '-'}
              />
            </BaseSpace>
          )
        },
      }),

      //updated_at
      convertColumnTable<{ updated_at: string }>({
        key: 'updated_at',
        title: 'Lần cuối cập nhật',
        sorter: true,
        render: (_, record) => {
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={
                record?.updated_at
                  ? moment(record?.updated_at)
                      ?.add(7, 'hours')
                      ?.format(FORMAT_DD_MM_YYYY_HH_MM)
                  : '-'
              }
            />
          )
        },
      }),

      ...(isPermissionUpdate
        ? [
            convertColumnTable<ITradeUnionMember>({
              key: 'id',
              fixed: 'right',
              title: (
                <BaseSpace size={8}>
                  <BaseText
                    children={'Đánh dấu đồng bộ'}
                    fontWeight="semibold"
                  />
                  <BaseTooltip title={`Toàn bộ được ${isSyncPage ? 'bỏ đánh dấu': 'đánh dấu'}`}>
                    <BaseSwitch
                      onChange={handleUpdateIsSync}
                      checked={isSyncPage}
                      loading={isLoadingUpdateMany}
                      disabled={!total}
                    />
                  </BaseTooltip>
                </BaseSpace>
              ),
              render: (_, record) => {
                return (
                  <BaseSwitch
                    checked={record?.isSync}
                    onChange={() => {
                      handleUpdate(record)
                    }}
                    loading={isLoadingUpdate}
                  />
                )
              },
            }),
          ]
        : []),
    ]
  }, [t, isSyncPage, data, flattenData?.metadata?.totalIsSync, total])

  const handleChangePagination = (
    pagination: Pagination,
    _,
    sorter: SorterResult<any> | SorterResult<any>[],
  ) => {
    setPagination(pagination)

    let sortParams: { field: string; order: 'ASC' | 'DESC' }[] = []

    if (Array.isArray(sorter)) {
      // Nếu sorter là mảng (multi-sort)
      sortParams = sorter
        .filter(s => s.order) // Lọc ra những cột có thứ tự sắp xếp
        .map(s => ({
          field: s.field as string,
          order: s.order === 'ascend' ? 'ASC' : 'DESC',
        }))
    } else if (sorter.order) {
      // Nếu chỉ có một cột được sort
      sortParams = [
        {
          field: sorter.field as string,
          order: sorter.order === 'ascend' ? 'ASC' : 'DESC',
        },
      ]
    }

    setSort(sortParams)
  }

  const downloadExcel = async (): Promise<void> => {
    await handleDownloadExcel({
      params: {
        search: filterDebounce?.search,
        isSync: filterDebounce?.isSync,
        unit: filterDebounce?.unit,
        sorts,
      },
      urlMethod: `${ENV_CONFIG.API_ENDPOINT}tum-hrm/download`,
      fileName: 'Đoàn viên công đoàn HRM',
    })
  }

  return {
    columns,
    pagination: {
      ...pagination,
      total,
    },
    onChangeFilter,
    filter,
    isLoading: isLoading || isLoadingUpdate || isLoadingUpdateMany,
    total,
    handleChangePagination,
    data,
    isPermissionSyncFromUeh,
    downloadExcel,
    isPermissionExportExcelFromUeh,
  }
}
